/**
 * SISTEMA DE SESSÃO CROSS-TAB ROBUSTO
 * 
 * Este módulo resolve todos os problemas de sessão entre abas:
 * 1. Compartilhamento de sessão via localStorage
 * 2. Sincronização de estado entre abas
 * 3. Detecção de novas abas sem logout
 * 4. Proteção contra race conditions
 */

interface SessionData {
  token: string;
  user: any;
  empresa: any;
  timestamp: number;
  lastActivity: number;
}

interface TabTracker {
  id: string;
  url: string;
  created: number;
  lastSeen: number;
  isActive: boolean;
}

export class CrossTabSession {
  private static instance: CrossTabSession;
  private tabId: string;
  private isInitialized = false;
  private sessionSyncInterval: NodeJS.Timeout | null = null;
  
  // Chaves para storage
  private readonly SESSION_KEY = 'cross_tab_session';
  private readonly TABS_KEY = 'cross_tab_tracker';
  private readonly LOGOUT_SYNC_KEY = 'cross_tab_logout';
  
  constructor() {
    this.tabId = this.generateTabId();
    this.setupTabTracker();
    this.setupSessionSync();
  }
  
  public static getInstance(): CrossTabSession {
    if (!CrossTabSession.instance) {
      CrossTabSession.instance = new CrossTabSession();
    }
    return CrossTabSession.instance;
  }
  
  /**
   * Gerar ID único para aba
   */
  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`;
  }
  
  /**
   * Configurar rastreamento de abas
   */
  private setupTabTracker(): void {
    // Registrar esta aba
    this.registerTab();
    
    // Limpar abas inativas periodicamente
    this.cleanupInactiveTabs();
    
    // Atualizar atividade desta aba
    this.startActivityTracking();
  }
  
  /**
   * Registrar esta aba como ativa
   */
  private registerTab(): void {
    const tabs = this.getActiveTabs();
    const tabInfo: TabTracker = {
      id: this.tabId,
      url: window.location.href,
      created: Date.now(),
      lastSeen: Date.now(),
      isActive: true
    };
    
    // Remover entrada anterior se existir
    const filteredTabs = tabs.filter(tab => tab.id !== this.tabId);
    filteredTabs.push(tabInfo);
    
    localStorage.setItem(this.TABS_KEY, JSON.stringify(filteredTabs));

  }
  
  /**
   * Obter abas ativas
   */
  private getActiveTabs(): TabTracker[] {
    try {
      const stored = localStorage.getItem(this.TABS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {

      return [];
    }
  }
  
  /**
   * Inicializar sessão - com proteção contra race conditions
   */
  public async initializeSession(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    // Verificar se há dados no sessionStorage (aba atual)
    const sessionToken = sessionStorage.getItem('token');
    const sessionUser = sessionStorage.getItem('user');
    const sessionEmpresa = sessionStorage.getItem('empresa');
    
    // Verificar se há dados compartilhados
    const sharedSession = this.getSharedSession();
    
    if (sessionToken && sessionUser && sessionEmpresa) {
      // Aba atual tem dados - sincronizar com outras abas

      const sessionData: SessionData = {
        token: sessionToken,
        user: JSON.parse(sessionUser),
        empresa: JSON.parse(sessionEmpresa),
        timestamp: Date.now(),
        lastActivity: Date.now()
      };
      
      this.saveSharedSession(sessionData);
      this.isInitialized = true;
      return true;
      
    } else if (sharedSession && this.isSessionValid(sharedSession)) {
      // Outra aba tem dados válidos - copiar para esta aba

      sessionStorage.setItem('token', sharedSession.token);
      sessionStorage.setItem('user', JSON.stringify(sharedSession.user));
      sessionStorage.setItem('empresa', JSON.stringify(sharedSession.empresa));
      
      // Atualizar atividade
      sharedSession.lastActivity = Date.now();
      this.saveSharedSession(sharedSession);
      
      this.isInitialized = true;
      return true;
      
    } else {
      // Nenhuma sessão válida encontrada

      this.clearSession();
      this.isInitialized = true;
      return false;
    }
  }
  
  /**
   * Verificar se sessão é válida
   */
  private isSessionValid(session: SessionData): boolean {
    if (!session.token || !session.user || !session.empresa) {
      return false;
    }
    
    // Verificar se a sessão não é muito antiga (24 horas)
    const maxAge = 24 * 60 * 60 * 1000; // 24 horas
    if (Date.now() - session.timestamp > maxAge) {
      return false;
    }
    
    // Verificar se não está inativa há muito tempo (2 horas)
    const maxInactivity = 2 * 60 * 60 * 1000; // 2 horas
    if (Date.now() - session.lastActivity > maxInactivity) {
      return false;
    }
    
    try {
      // Verificar se o token é válido (básico)
      const tokenParts = session.token.split('.');
      if (tokenParts.length !== 3) {
        return false;
      }
      
      // Decodificar payload para verificar expiração
      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Date.now() / 1000;
      
      // Verificar se token não expirou (com margem de 30 segundos)
      if (payload.exp && payload.exp < (currentTime + 30)) {
        return false;
      }
      
      return true;
    } catch (e) {

      return false;
    }
  }
  
  /**
   * Obter sessão compartilhada
   */
  private getSharedSession(): SessionData | null {
    try {
      const stored = localStorage.getItem(this.SESSION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {

      return null;
    }
  }
  
  /**
   * Salvar sessão compartilhada
   */
  private saveSharedSession(session: SessionData): void {
    try {
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    } catch (e) {
      console.error('[CROSS-TAB] Erro ao salvar sessão compartilhada:', e);
    }
  }
  
  /**
   * Verificar se usuário está autenticado (cross-tab)
   */
  public isAuthenticated(): boolean {
    const sharedSession = this.getSharedSession();
    
    if (!sharedSession) {
      return false;
    }
    
    return this.isSessionValid(sharedSession);
  }
  
  /**
   * Fazer login e sincronizar com todas as abas
   */
  public login(token: string, user: any, empresa: any): void {

    // Salvar no sessionStorage da aba atual
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(user));
    sessionStorage.setItem('empresa', JSON.stringify(empresa));
    
    // Salvar sessão compartilhada
    const sessionData: SessionData = {
      token,
      user,
      empresa,
      timestamp: Date.now(),
      lastActivity: Date.now()
    };
    
    this.saveSharedSession(sessionData);
    
    // Notificar outras abas
    this.notifyOtherTabs('login', { sessionData });
  }
  
  /**
   * Fazer logout e sincronizar com todas as abas
   */
  public logout(): void {

    // Limpar sessão
    this.clearSession();
    
    // Notificar outras abas
    this.notifyOtherTabs('logout', { timestamp: Date.now(), tabId: this.tabId });
  }
  
  /**
   * Limpar sessão
   */
  private clearSession(): void {
    // Limpar sessionStorage
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('empresa');
    
    // Limpar localStorage
    localStorage.removeItem(this.SESSION_KEY);
  }
  
  /**
   * Notificar outras abas
   */
  private notifyOtherTabs(action: string, data: any): void {
    const notification = {
      action,
      data,
      timestamp: Date.now(),
      fromTab: this.tabId
    };
    
    // Usar localStorage para comunicação entre abas
    localStorage.setItem(`${this.LOGOUT_SYNC_KEY}_${Date.now()}`, JSON.stringify(notification));
    
    // Limpar notificações antigas após um tempo
    setTimeout(() => {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.LOGOUT_SYNC_KEY)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const notif = JSON.parse(stored);
              if (Date.now() - notif.timestamp > 30000) { // 30 segundos
                localStorage.removeItem(key);
              }
            }
          } catch (e) {
            localStorage.removeItem(key);
          }
        }
      });
    }, 5000);
  }
  
  /**
   * Configurar sincronização de sessão
   */
  private setupSessionSync(): void {
    // Escutar mudanças no localStorage
    window.addEventListener('storage', (event) => {
      if (event.key?.startsWith(this.LOGOUT_SYNC_KEY)) {
        this.handleCrossTabNotification(event);
      }
    });
    
    // Sync periódico
    this.sessionSyncInterval = setInterval(() => {
      this.syncWithOtherTabs();
    }, 30000); // A cada 30 segundos
  }
  
  /**
   * Lidar com notificações entre abas
   */
  private handleCrossTabNotification(event: StorageEvent): void {
    if (!event.newValue) return;
    
    try {
      const notification = JSON.parse(event.newValue);
      
      // Ignorar notificações desta própria aba
      if (notification.fromTab === this.tabId) {
        return;
      }

      if (notification.action === 'logout') {
        // Limpar sessão local
        this.clearSession();
        
        // Redirecionar para login se não estivermos lá
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      } else if (notification.action === 'login') {
        // Copiar dados da sessão
        const sessionData = notification.data.sessionData;
        sessionStorage.setItem('token', sessionData.token);
        sessionStorage.setItem('user', JSON.stringify(sessionData.user));
        sessionStorage.setItem('empresa', JSON.stringify(sessionData.empresa));

      }
    } catch (e) {

    }
  }
  
  /**
   * Sincronizar com outras abas
   */
  private syncWithOtherTabs(): void {
    if (!this.isInitialized) return;
    
    const sharedSession = this.getSharedSession();
    
    if (sharedSession && this.isSessionValid(sharedSession)) {
      // Atualizar atividade
      sharedSession.lastActivity = Date.now();
      this.saveSharedSession(sharedSession);
      
      // Atualizar atividade da aba
      this.updateTabActivity();
    }
  }
  
  /**
   * Rastreamento de atividade da aba
   */
  private startActivityTracking(): void {
    // Atualizar atividade a cada minuto
    setInterval(() => {
      this.updateTabActivity();
    }, 60000);
    
    // Atualizar atividade em eventos do usuário
    const events = ['click', 'keydown', 'scroll', 'focus'];
    events.forEach(event => {
      document.addEventListener(event, () => {
        this.updateTabActivity();
      }, { passive: true });
    });
  }
  
  /**
   * Atualizar atividade da aba
   */
  private updateTabActivity(): void {
    const tabs = this.getActiveTabs();
    const updatedTabs = tabs.map(tab => {
      if (tab.id === this.tabId) {
        return { ...tab, lastSeen: Date.now(), isActive: true };
      }
      return tab;
    });
    
    localStorage.setItem(this.TABS_KEY, JSON.stringify(updatedTabs));
  }
  
  /**
   * Limpar abas inativas
   */
  private cleanupInactiveTabs(): void {
    setInterval(() => {
      const tabs = this.getActiveTabs();
      const now = Date.now();
      const activeTabs = tabs.filter(tab => now - tab.lastSeen < 300000); // 5 minutos
      
      if (activeTabs.length !== tabs.length) {
        localStorage.setItem(this.TABS_KEY, JSON.stringify(activeTabs));

      }
    }, 120000); // A cada 2 minutos
  }
  
  /**
   * Obter informações da sessão
   */
  public getSessionInfo() {
    const sharedSession = this.getSharedSession();
    const activeTabs = this.getActiveTabs();
    
    return {
      tabId: this.tabId,
      isAuthenticated: this.isAuthenticated(),
      activeTabsCount: activeTabs.length,
      sessionValid: sharedSession ? this.isSessionValid(sharedSession) : false,
      lastActivity: sharedSession?.lastActivity || 0,
      sessionAge: sharedSession ? Date.now() - sharedSession.timestamp : 0
    };
  }
  
  /**
   * Cleanup ao fechar aba
   */
  public cleanup(): void {
    if (this.sessionSyncInterval) {
      clearInterval(this.sessionSyncInterval);
    }
    
    // Remover esta aba da lista
    const tabs = this.getActiveTabs();
    const filteredTabs = tabs.filter(tab => tab.id !== this.tabId);
    localStorage.setItem(this.TABS_KEY, JSON.stringify(filteredTabs));

  }
}

// Instância singleton
export const crossTabSession = CrossTabSession.getInstance();

// Cleanup automático ao fechar janela
window.addEventListener('beforeunload', () => {
  crossTabSession.cleanup();
});

export default crossTabSession;
